import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel, SuggestionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const productsDataView = getProductListDataView(state);
    if (productsDataView.value) {
        return {
            loaded: true,
            searchPath: getSettingsCollection(state).searchSettings.searchPath,
            didYouMeanSuggestions: productsDataView.didYouMeanSuggestions,
            correctedQuery: productsDataView.correctedQuery,
            originalQuery: productsDataView.originalQuery,
            products: productsDataView.value,
        };
    }
    return {
        loaded: false,
        searchPath: "",
        didYouMeanSuggestions: [] as SuggestionModel[] | null,
        correctedQuery: "",
        originalQuery: "",
        products: [] as ProductModel[] | undefined,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ProductListSuggestionStyles {
    wrapper?: InjectableCss;
}

export const suggestionStyles: ProductListSuggestionStyles = {
    wrapper: {
        css: css`
            &.noSearchResult {
                margin-left: 12px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin: 12px auto;
                                text-align: center;
                            `,
                            css`
                                margin: 12px auto;
                                text-align: center;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
};

const styles = suggestionStyles;

const ProductListSuggestion = ({
    loaded,
    searchPath,
    didYouMeanSuggestions,
    originalQuery,
    correctedQuery,
    products,
}: Props) => {
    if (!loaded) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper} className={products && products.length === 0 ? "noSearchResult" : ""}>
            {didYouMeanSuggestions && didYouMeanSuggestions.length > 0 && (
                <>
                    <Typography>{translate("Did you mean ")}</Typography>
                    <Link href={`/${searchPath}?query=${didYouMeanSuggestions[0].suggestion}`}>
                        {didYouMeanSuggestions[0].suggestion}
                    </Link>
                </>
            )}
            {correctedQuery && originalQuery !== correctedQuery && (
                <>
                    <Typography>{translate("Search instead for ")}</Typography>
                    <Link
                        href={`/${searchPath}?query=${originalQuery}&includeSuggestions=false`}
                        data-test-selector="productListOriginalQuery"
                    >
                        {originalQuery}
                    </Link>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListSuggestion),
    definition: {
        group: "Product List",
        displayName: "Suggestion",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
